/**
 * Call-staff buttons.
 *
 * EVERY button sends a BUSINESS CODE, never the label the guest is reading.
 * `CALL_STAFF`, `REQUEST_WATER`, `REQUEST_UTENSILS`, `REQUEST_CLEAN_TABLE`,
 * `REQUEST_PAYMENT` are the same five strings whether the guest tapped
 * "Gọi nhân viên", "呼叫服务员" or "Позвать официанта" — which is what lets the
 * staff app, the dispatcher and the KPI engine act on them without ever seeing
 * a translation.
 *
 * The free-text box travels separately in `freeText` and is stored with the
 * guest's locale, so staff know it needs translating rather than guessing.
 */

import { useState } from 'react';
import { LoadingState } from '@sonauto/ui';
import { useCustomerI18n } from './CustomerI18nProvider';
import { usePoll, errorMessage } from '../lib/hooks';
import type { CustomerApi } from '../lib/api';
import type { CustomerRequestCode, CustomerRequestsResponse } from '../lib/types';

/** Code → bundle key + Vietnamese last resort + icon. Codes are the contract. */
const REQUESTS: ReadonlyArray<{ code: CustomerRequestCode; key: string; vi: string; icon: string; tone: string }> = [
  { code: 'CALL_STAFF', key: 'customer.call_staff', vi: 'Gọi nhân viên', icon: '🔔', tone: 'bg-brand-600 text-white' },
  { code: 'REQUEST_WATER', key: 'customer.request_water', vi: 'Xin thêm nước', icon: '💧', tone: 'bg-surface ring-1 ring-line' },
  {
    code: 'REQUEST_UTENSILS',
    key: 'customer.request_utensils',
    vi: 'Xin thêm bát đũa',
    icon: '🍴',
    tone: 'bg-surface ring-1 ring-line',
  },
  {
    code: 'REQUEST_CLEAN_TABLE',
    key: 'customer.request_clean_table',
    vi: 'Xin dọn bàn',
    icon: '🧽',
    tone: 'bg-surface ring-1 ring-line',
  },
  {
    code: 'REQUEST_PAYMENT',
    key: 'customer.request_payment',
    vi: 'Xin thanh toán',
    icon: '💳',
    tone: 'bg-success-solid text-white',
  },
];

export function HelpScreen({ api }: { readonly api: CustomerApi }): React.JSX.Element {
  const { tOr, locale } = useCustomerI18n();
  const [freeText, setFreeText] = useState('');
  const [busy, setBusy] = useState<CustomerRequestCode | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const requests = usePoll<CustomerRequestsResponse>((signal) => api.requests({ signal }), 15_000, [api]);

  const send = async (code: CustomerRequestCode): Promise<void> => {
    setBusy(code);
    setFeedback(null);
    try {
      const response = await api.request(code, freeText.trim() === '' ? null : freeText.trim());
      setFreeText('');
      setFeedback(
        response.message ??
          (response.duplicate
            ? tOr('customer.request_pending', 'Yêu cầu của bạn đang được xử lý.')
            : tOr('customer.request_sent', 'Đã gửi yêu cầu tới nhân viên.')),
      );
      requests.reload();
    } catch (cause: unknown) {
      setFeedback(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-3 py-4">
      <h2 className="text-flow text-lg font-bold">{tOr('customer.nav.help', 'Gọi nhân viên')}</h2>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {REQUESTS.map((request) => (
          <button
            key={request.code}
            type="button"
            disabled={busy !== null}
            onClick={() => void send(request.code)}
            className={`text-flow flex min-h-[64px] items-center gap-3 rounded-lg px-4 py-3 text-base font-semibold disabled:opacity-60 ${request.tone}`}
          >
            <span aria-hidden="true" className="text-2xl">
              {request.icon}
            </span>
            <span className="text-flow text-left">{tOr(request.key, request.vi)}</span>
          </button>
        ))}
      </div>

      <label className="text-flow mt-5 block text-sm font-bold" htmlFor="help-note">
        {tOr('customer.note.label', 'Ghi chú thêm')}
      </label>
      <textarea
        id="help-note"
        rows={3}
        maxLength={500}
        lang={locale}
        value={freeText}
        onChange={(e) => setFreeText(e.target.value)}
        placeholder={tOr('customer.feedback.placeholder', 'Bạn cần hỗ trợ gì thêm?')}
        className="mt-1 w-full rounded-md bg-surface-alt p-3 text-base ring-1 ring-line"
      />
      <p className="text-flow mt-1 text-xs text-fg-subtle">
        {tOr('customer.language.machine_translated_notice', 'Nhân viên sẽ nhận ghi chú kèm ngôn ngữ bạn đang dùng.')}
      </p>

      {feedback !== null ? (
        <p className="text-flow mt-3 rounded-lg bg-info-bg p-3 text-sm font-semibold text-info-fg">{feedback}</p>
      ) : null}

      <h3 className="text-flow mt-6 text-base font-bold">
        {tOr('customer.request_acknowledged', 'Yêu cầu gần đây')}
      </h3>
      {requests.initialLoading ? <LoadingState compact /> : null}
      <ul className="mt-2 space-y-2">
        {(requests.data?.requests ?? []).map((request, index) => (
          <li
            key={`${request.requestCode}-${request.requestedAt}-${index}`}
            className="flex items-start justify-between gap-2 rounded-lg bg-surface p-3 text-sm ring-1 ring-line"
          >
            <span className="text-flow min-w-0 flex-1">
              {/* `label` is the server's translation of the code; the bundle
                  string is the fallback. The CODE is never shown to a guest. */}
              {request.label ??
                tOr(
                  REQUESTS.find((r) => r.code === request.requestCode)?.key ?? 'customer.call_staff',
                  REQUESTS.find((r) => r.code === request.requestCode)?.vi ?? 'Yêu cầu',
                )}
              {request.freeText !== null ? (
                <span className="block text-xs text-fg-subtle">“{request.freeText}”</span>
              ) : null}
            </span>
            <span className="shrink-0 text-xs text-fg-muted">
              {request.resolvedAt === null
                ? tOr('customer.request_pending', 'Đang xử lý')
                : tOr('customer.request_acknowledged', 'Đã xử lý')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
